import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import SignatureCanvas from 'react-signature-canvas';
import { X, Eraser, Eye, Sparkles, ClipboardList } from 'lucide-react';
import DatePickerField from './DatePickerField';
import { AttendanceGrid, BatchView, TC_API, fmtD, openPdf } from './TrainingCardsParts';
import TrainingSignInModal from './TrainingSignInModal';

const inp = 'bg-white border border-slate-300 rounded-lg px-2 py-1.5 text-sm text-slate-900 focus:outline-none focus:border-purple-500 w-full';
const plusYears = (d, y) => { if (!d) return ''; const [Y, M, D] = d.split('-').map(Number); return `${Y + y}-${String(M).padStart(2, '0')}-${String(D).padStart(2, '0')}`; };

// HR door: /api/training-cards/session/{id}/… ; trainer door (no login): /api/trainer/{token}/…
export const hrApi = (sessionId) => ({
  prep: `${TC_API}/session/${sessionId}/prep`, preview: `${TC_API}/session/${sessionId}/preview`, generate: `${TC_API}/session/${sessionId}/generate`,
  signOff: `${TC_API}/session/${sessionId}/sign-off.pdf`, sign: `${TC_API}/session/${sessionId}/sign`,
  batch: (id) => `${TC_API}/batches/${id}`, pdf: (id) => `${TC_API}/batches/${id}/pdf`, batchSignOff: (id) => `${TC_API}/batches/${id}/sign-off.pdf`,
});
export const trainerApi = (token) => {
  const b = `${process.env.REACT_APP_BACKEND_URL}/api/trainer/${token}`;
  return { prep: `${b}/prep`, preview: `${b}/preview`, generate: `${b}/generate`, signOff: `${b}/sign-off.pdf`, sign: `${b}/sign`,
    batch: (id) => `${b}/batches/${id}`, pdf: (id) => `${b}/batches/${id}/pdf`, batchSignOff: (id) => `${b}/batches/${id}/sign-off.pdf` };
};

// The wrap-up itself: date → courses → attendance (+ walk-ins) → signature → generate → batch progress.
export function WrapUpFlow({ api, onPrep, onLoadError, onClose, onDone, trainerMode = false }) {
  const [prep, setPrep] = useState(null);
  const [attendees, setAttendees] = useState([]);
  const [trainingDate, setTrainingDate] = useState('');
  const [trainer, setTrainer] = useState('Chad');
  const [courses, setCourses] = useState([]);
  const [att, setAtt] = useState({});
  const [hasInk, setHasInk] = useState(false);
  const [preview, setPreview] = useState(null);
  const [batch, setBatch] = useState(null);
  const [busy, setBusy] = useState(false);
  const [signed, setSigned] = useState({});
  const [signIn, setSignIn] = useState(null);
  const [reuseSig, setReuseSig] = useState(false);
  const sig = useRef(null);

  const prepUrl = api.prep;
  const cb = useRef({ onPrep, onLoadError });
  cb.current = { onPrep, onLoadError };
  useEffect(() => {
    axios.get(prepUrl).then((r) => {
      const p = r.data;
      setPrep(p);
      cb.current.onPrep?.(p);
      setAttendees(p.attendees);
      setTrainingDate(p.training_date);
      setTrainer(p.trainer_name || 'Chad');
      setSigned(p.signed || {});
      setReuseSig(!!p.reusable_signature);
      setCourses(p.courses.map((c) => ({ ...c, checked: c.in_session, touched: false })));
      const on = p.courses.filter((c) => c.in_session).map((c) => c.topic);
      setAtt(Object.fromEntries(p.attendees.map((a) => [a.employee_id, { attended: true, topics: new Set(on) }])));
      if (p.issued) setBatch(p.issued);
    }).catch((e) => { cb.current.onLoadError ? cb.current.onLoadError(e) : toast.error(e.response?.data?.detail || 'Could not load the session'); });
  }, [prepUrl]);

  useEffect(() => {
    if (!batch || batch.status !== 'running') return undefined;
    const t = setInterval(() => axios.get(api.batch(batch.id)).then((r) => { setBatch(r.data); if (r.data.status !== 'running') { onDone?.(); } }).catch(() => {}), 1200);
    return () => clearInterval(t);
  }, [batch, onDone, api]);

  const changeDate = (d) => {
    setTrainingDate(d);
    setCourses((cs) => cs.map((c) => (c.touched ? c : { ...c, expiry: plusYears(d, prep?.default_years || 3) })));
  };
  const setCourse = (topic, upd) => setCourses((cs) => cs.map((c) => (c.topic === topic ? { ...c, ...upd } : c)));
  const toggleCourse = (topic) => {
    const c = courses.find((x) => x.topic === topic);
    setCourse(topic, { checked: !c.checked });
    setAtt((p) => Object.fromEntries(Object.entries(p).map(([id, row]) => { const s = new Set(row.topics); if (!c.checked && row.attended) s.add(topic); if (c.checked) s.delete(topic); return [id, { ...row, topics: s }]; })));
  };
  const addWalkIn = (person) => {
    setAttendees((as) => [...as, { ...person, walk_in: true, booked_topics: [] }]);
    setAtt((p) => ({ ...p, [person.employee_id]: { attended: true, topics: new Set(courses.filter((c) => c.checked).map((c) => c.topic)) } }));
    toast.success(`${person.name} added as a walk-in`);
  };
  const clearSig = () => { sig.current?.clear(); setHasInk(false); setPreview(null); };
  const sigData = () => (sig.current && !sig.current.isEmpty() ? sig.current.getCanvas().toDataURL('image/png') : null);
  const canReuse = !!prep?.reusable_signature;
  const usingReuse = canReuse && reuseSig;
  // a top-up batch (someone was missed) starts with nothing ticked so nobody gets a second card by accident
  const startTopUp = () => { setBatch(null); setAtt((p) => Object.fromEntries(Object.keys(p).map((id) => [id, { attended: false, topics: new Set() }]))); toast.info('Tick just the people and courses that were missed'); };

  const showPreview = async () => {
    const c = courses.find((x) => x.checked) || courses[0];
    try {
      const r = await axios.post(api.preview, { course: c, training_date: trainingDate, trainer_name: trainer, signature: sigData(), reuse_signature: usingReuse && !sigData(), employee_name: attendees[0]?.name }, { responseType: 'blob' });
      setPreview(URL.createObjectURL(r.data));
    } catch (e) { toast.error(e.response?.data?.detail || `Preview failed: ${e.message}`); }
  };

  const cardCount = attendees.reduce((n, a) => n + courses.filter((c) => c.checked && att[a.employee_id]?.topics.has(c.topic)).length, 0);
  const payload = (signature, force) => ({
    training_date: trainingDate, trainer_name: trainer, signature, force, reuse_signature: !signature && usingReuse,
    courses: courses.filter((c) => c.checked).map((c) => ({ topic: c.topic, title: c.title, detail: c.detail, expiry: c.expiry })),
    attendance: Object.entries(att).filter(([, row]) => row.attended).map(([id, row]) => ({ employee_id: id, topics: [...row.topics] })),
  });
  const signOffSheet = () => openPdf(api.signOff, payload(sigData(), false));
  const generate = async (force = false) => {
    const signature = sigData();
    if (!signature && !usingReuse) { toast.error(`${trainer || 'The trainer'} needs to sign first`); return; }
    if (!cardCount) { toast.error('Tick at least one person and one course'); return; }
    setBusy(true);
    try {
      const r = await axios.post(api.generate, payload(signature, force));
      toast.success(`Making ${r.data.total} cards…`);
      setBatch({ id: r.data.batch_id, status: 'running', total: r.data.total, done: 0, failed: 0, cards: [] });
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not generate cards'); }
    setBusy(false);
  };

  if (!prep) return <p className="p-6 text-slate-700 text-sm" data-testid="tc-loading">Loading…</p>;

  if (batch) {
    return (
      <div className="p-4 space-y-3">
        <BatchView batch={batch} pdfUrl={api.pdf(batch.id)} signOffUrl={api.batchSignOff(batch.id)} />
        {batch.status === 'done' && (
          <button onClick={startTopUp} className="px-3 py-2 rounded-lg text-sm bg-white border border-slate-300 text-slate-800 hover:bg-slate-100" data-testid="tc-issue-again">
            Missed someone or a course? Issue more cards for this session
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="p-4 space-y-5">
      <div className="grid gap-3 sm:grid-cols-3">
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">1. Training date</label>
          <DatePickerField value={trainingDate} onChange={changeDate} className="mt-1 w-full bg-white rounded-lg px-3 py-2 text-sm" testid="tc-training-date" clearable={false} />
        </div>
        <div>
          <label className="text-[11px] uppercase tracking-wider text-slate-500 font-bold">Trainer</label>
          <input value={trainer} onChange={(e) => setTrainer(e.target.value)} className={`${inp} mt-1`} data-testid="tc-trainer" />
        </div>
        <div className="text-xs text-slate-500 self-end pb-2">Cards show <b className="text-slate-800">Trained</b> and <b className="text-slate-800">Expires</b>; expiry defaults to {prep.default_years} years — change it per course below.</div>
      </div>

      <div data-testid="tc-courses">
        <p className="text-sm font-semibold text-slate-900 mb-2">2. Courses covered <span className="text-slate-500 font-normal text-xs">— one card per course per person</span></p>
        <div className="space-y-1.5">
          {courses.map((c) => (
            <div key={c.topic} className={`grid gap-2 items-center rounded-lg px-2 py-1.5 border ${c.checked ? 'bg-purple-50 border-purple-200' : 'bg-white border-slate-200'} sm:grid-cols-[auto_1fr_1.4fr_150px]`} data-testid={`tc-course-${c.topic}`}>
              <label className="inline-flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={c.checked} onChange={() => toggleCourse(c.topic)} className="w-5 h-5 accent-purple-500" data-testid={`tc-course-check-${c.topic}`} />
                <span className="sm:hidden text-sm text-slate-900 font-semibold">{c.title}</span>
                {!c.in_session && <span className="text-[10px] text-slate-500 uppercase">extra</span>}
              </label>
              <input value={c.title} disabled={!c.checked} onChange={(e) => setCourse(c.topic, { title: e.target.value })} className={`${inp} font-semibold hidden sm:block`} data-testid={`tc-course-title-${c.topic}`} />
              <input value={c.detail} disabled={!c.checked} onChange={(e) => setCourse(c.topic, { detail: e.target.value })} placeholder="Detail line (e.g. classes)" className={`${inp} hidden sm:block`} data-testid={`tc-course-detail-${c.topic}`} />
              <input type="date" value={c.expiry} disabled={!c.checked} onChange={(e) => setCourse(c.topic, { expiry: e.target.value, touched: true })} className={`${inp} hidden sm:block`} title="Expiry on the card" data-testid={`tc-course-expiry-${c.topic}`} />
            </div>
          ))}
        </div>
      </div>

      <AttendanceGrid attendees={attendees} courses={courses} att={att} setAtt={setAtt} pool={prep.walk_in_pool} onAddWalkIn={addWalkIn}
        signed={signed} onSign={(id) => setSignIn({ startId: id })} />
      {signIn && (
        <TrainingSignInModal attendees={attendees.filter((a) => att[a.employee_id]?.attended)} signed={signed} startId={signIn.startId} signUrl={api.sign}
          onSigned={setSigned} onClose={() => setSignIn(null)} />
      )}

      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-slate-900">4. {trainer || 'Trainer'} signs here</p>
            {canReuse && (
              <label className="text-xs text-slate-700 flex items-center gap-1.5 cursor-pointer" data-testid="tc-reuse-sig-label">
                <input type="checkbox" checked={reuseSig} onChange={(e) => setReuseSig(e.target.checked)} className="w-4 h-4 accent-emerald-500" data-testid="tc-reuse-sig" />
                Use {prep.reusable_signature.trainer_name}'s signature from the earlier batch
              </label>
            )}
          </div>
          {usingReuse ? (
            <div className="rounded-xl border border-emerald-200 bg-emerald-50 h-[180px] flex flex-col items-center justify-center text-center px-4" data-testid="tc-reuse-sig-note">
              <p className="text-emerald-800 text-sm font-semibold">✓ {prep.reusable_signature.trainer_name}'s signature from today's batch will go on these cards</p>
              <p className="text-[11px] text-slate-500 mt-1">Untick above if {trainer} is here and wants to sign again.</p>
            </div>
          ) : (
            <div className="relative bg-white rounded-xl overflow-hidden border-2 border-dashed border-slate-500" data-testid="tc-sig-canvas">
              <SignatureCanvas ref={sig} penColor="#0b1f3a" canvasProps={{ className: 'w-full touch-none', style: { width: '100%', height: '180px' } }} onEnd={() => { setHasInk(true); setPreview(null); }} />
              {hasInk && (
                <button type="button" onClick={clearSig} className="absolute bottom-2 right-2 px-3 py-1.5 rounded-lg text-xs font-semibold bg-slate-50 text-slate-900 border border-slate-300 hover:bg-slate-50 flex items-center gap-1.5 shadow" data-testid="tc-sig-clear">
                  <Eraser className="w-3.5 h-3.5" /> Erase &amp; sign again
                </button>
              )}
              {!hasInk && <span className="absolute inset-x-0 bottom-2 text-center text-[11px] text-slate-500 pointer-events-none">sign here with a finger or stylus</span>}
            </div>
          )}
          <p className="text-[11px] text-slate-500 mt-1">This signature goes on every card in this batch. Not happy with it? Tap <b>Erase &amp; sign again</b> on the pad.</p>
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <p className="text-sm font-semibold text-slate-900">Card preview</p>
            <button onClick={showPreview} className="text-xs text-purple-800 hover:text-slate-900 flex items-center gap-1" data-testid="tc-preview-btn"><Eye className="w-3.5 h-3.5" /> {preview ? 'Refresh' : 'Show a sample card'}</button>
          </div>
          {preview ? <img src={preview} alt="Card preview" className="rounded-xl border border-slate-300 w-full shadow-lg" data-testid="tc-preview-img" />
            : <div className="rounded-xl border border-slate-200 bg-white h-[180px] flex items-center justify-center text-xs text-slate-500 text-center px-4">365-branded wallet card · C&amp;M Safety · trained / expires · signature</div>}
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 pt-2 border-t border-slate-200">
        <p className="text-xs text-slate-500">{cardCount} card{cardCount !== 1 ? 's' : ''} · filed to each person's Drive safety file · old cards for the same course move to Expired Tickets</p>
        <div className="flex gap-2">
          <button onClick={signOffSheet} disabled={!cardCount} className="px-3 py-2 rounded-lg text-sm bg-white border border-slate-300 text-slate-800 hover:bg-slate-100 disabled:opacity-40 flex items-center gap-1.5" title="Printable attendance sheet with a signature line per person — for the trainer's records" data-testid="tc-signoff">
            <ClipboardList className="w-4 h-4" /> Sign-off sheet
          </button>
          {!trainerMode && <button onClick={onClose} className="px-3 py-2 rounded-lg text-sm bg-white border border-slate-300 text-slate-800" data-testid="tc-later">Later</button>}
          <button onClick={() => generate(!!prep.issued)} disabled={busy || (!hasInk && !usingReuse) || !cardCount} className="px-4 py-2 rounded-lg text-sm font-bold bg-purple-600 hover:bg-purple-500 text-white disabled:opacity-40 flex items-center gap-2" data-testid="tc-generate">
            <Sparkles className="w-4 h-4" /> Generate {cardCount || ''} cards
          </button>
        </div>
      </div>
    </div>
  );
}

// HR's modal around the flow.
export default function TrainingWrapUpModal({ session, onClose, onDone }) {
  const api = React.useMemo(() => hrApi(session.id), [session.id]);
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-start sm:items-center justify-center p-2 sm:p-6 overflow-y-auto" data-testid="tc-modal">
      <div className="bg-white border border-slate-200 rounded-2xl w-full max-w-4xl my-4 shadow-2xl">
        <div className="flex items-start justify-between gap-3 p-4 border-b border-slate-200">
          <div>
            <p className="text-slate-900 font-bold text-lg flex items-center gap-2"><Sparkles className="w-5 h-5 text-purple-700" /> Wrap up &amp; issue training cards</p>
            <p className="text-xs text-slate-500">{session.provider_name} · {fmtD(session.start_date)}{session.end_date !== session.start_date ? ` – ${fmtD(session.end_date)}` : ''}{session.location ? ` · ${session.location}` : ''} · {session.attendees?.length} booked</p>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg text-slate-500 hover:text-slate-900 hover:bg-slate-50" data-testid="tc-close"><X className="w-5 h-5" /></button>
        </div>
        <WrapUpFlow api={api} onClose={onClose} onDone={onDone} onLoadError={(e) => { toast.error(e.response?.data?.detail || 'Could not load the session'); onClose(); }} />
      </div>
    </div>
  );
}
